import { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, limit, doc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from 'src/config/firebase';

export interface TrainerNotificationItem {
  id: string;
  title: string;
  body: string;
  type?: 'package_request' | 'payment' | 'system' | string;
  chat_id?: string;
  message_id?: string;
  sender_uid?: string;
  sender_name?: string;
  sender_avatar?: string;
  is_read: boolean;
  created_at?: unknown;
}

export const useTrainerNotifications = (trainerUid?: string) => {
  const [notifications, setNotifications] = useState<TrainerNotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeUid, setActiveUid] = useState(
    () => auth.currentUser?.uid || (trainerUid && !trainerUid.includes('@') ? trainerUid : ''),
  );

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        setActiveUid(user.uid);
      } else {
        setIsLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const uidToUse =
    activeUid ||
    auth.currentUser?.uid ||
    (trainerUid && !trainerUid.includes('@') ? trainerUid : '');

  useEffect(() => {
    if (!uidToUse) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const notifQuery = query(
      collection(db, 'trainer_app_users', uidToUse, 'notifications'),
      orderBy('created_at', 'desc'),
      limit(30),
    );

    const unsubscribe = onSnapshot(
      notifQuery,
      (snapshot) => {
        const items: TrainerNotificationItem[] = [];
        snapshot.forEach((d) => {
          const data = d.data() as Partial<TrainerNotificationItem>;
          items.push({
            id: d.id,
            title: data.title || 'Notification',
            body: data.body || '',
            type: data.type || 'system',
            chat_id: data.chat_id,
            message_id: data.message_id,
            sender_uid: data.sender_uid,
            sender_name: data.sender_name,
            sender_avatar: data.sender_avatar,
            is_read: Boolean(data.is_read),
            created_at: data.created_at,
          });
        });
        setNotifications(items);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching trainer notifications:', error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [uidToUse]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!uidToUse || !notificationId) return;
      try {
        const notifDocRef = doc(db, 'trainer_app_users', uidToUse, 'notifications', notificationId);
        await updateDoc(notifDocRef, { is_read: true });
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    },
    [uidToUse],
  );

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
  };
};
